import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

const radioGroupItemVariants = cva(
  'flex aspect-square size-4 shrink-0 items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      disabled: {
        true: 'border-main-border-disabled bg-main-background-interactive-input-disabled',
        false: 'border-main-border-semiBold bg-main-background-card hover:border-main-border-focus',
      },
      error: {
        true: 'border-main-border-error',
      },
    },
    defaultVariants: {
      disabled: false,
      error: false,
    },
  },
)

const radioIndicatorVariants = cva('size-2 rounded-full', {
  variants: {
    disabled: {
      true: 'bg-main-foreground-disabled',
      false: 'bg-main-foreground-accent',
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, 'disabled'> & {
    disabled?: boolean
    error?: boolean
  }
>(({ className, disabled, error, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ disabled, error }), className)}
      disabled={disabled}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className='flex items-center justify-center'>
        {<div className={cn(radioIndicatorVariants({ disabled }))} />}
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export interface RadioOption<T extends string | number = string> {
  label: React.ReactNode
  value: T
  disabled?: boolean
}

export interface RadioGroupProps<T extends string | number = string> extends Omit<
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
  'disabled' | 'value' | 'onValueChange'
> {
  label?: React.ReactNode
  subtitle?: React.ReactNode
  direction?: 'vertical' | 'horizontal'
  options?: RadioOption<T>[]
  value?: T
  onValueChange?: (value: T) => void
  disabled?: boolean
  error?: boolean
}

function RadioGroupInner<T extends string | number = string>(
  {
    className,
    label,
    subtitle,
    direction = 'vertical',
    options,
    value,
    onValueChange,
    disabled,
    error,
    children,
    ...props
  }: RadioGroupProps<T>,
  ref: React.ForwardedRef<React.ElementRef<typeof RadioGroupPrimitive.Root>>,
) {
  const handleValueChange = (strValue: string) => {
    if (!onValueChange || !options) return
    const option = options.find((o) => String(o.value) === strValue)
    if (option) onValueChange(option.value)
  }

  if (options && options.length > 0) {
    return (
      <div>
        {label && (
          <legend className={cn('block text-label-default', subtitle ? '' : 'mb-1')}>
            {label}
          </legend>
        )}
        {subtitle && (
          <p className={'mb-1.5 text-start text-main-foreground-tertiary text-body-small'}>
            {subtitle}
          </p>
        )}
        <RadioGroupPrimitive.Root
          className={cn(
            'flex',
            direction === 'vertical' ? 'flex-col gap-2' : 'flex-row flex-wrap items-center gap-4',
            className,
          )}
          value={value != null ? String(value) : undefined}
          onValueChange={handleValueChange}
          {...props}
          ref={ref}
        >
          {options.map((option) => (
            <label
              key={String(option.value)}
              className={cn(
                'flex cursor-pointer select-none items-center gap-2',
                (disabled || option.disabled) && 'cursor-not-allowed',
              )}
            >
              <RadioGroupItem
                value={String(option.value)}
                id={String(option.value)}
                disabled={disabled || option.disabled}
                error={error}
              />
              <span className={'text-main-foreground-primary text-body-default'}>
                {option.label}
              </span>
            </label>
          ))}
        </RadioGroupPrimitive.Root>
      </div>
    )
  }

  // legacy -- backward compatibility
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      disabled={disabled}
      value={value != null ? String(value) : undefined}
      onValueChange={onValueChange as ((value: string) => void) | undefined}
      {...props}
      ref={ref}
    >
      {children}
    </RadioGroupPrimitive.Root>
  )
}

const RadioGroup = React.forwardRef(RadioGroupInner) as <T extends string | number = string>(
  props: RadioGroupProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement

export { RadioGroup, RadioGroupItem }
